import type { HealthCheckResponse } from '../types/api'
import { createHeaders } from './authHeaders'
import { ApiError, problemDetailText } from './apiError'

/**
 * `GET /api/health` — the API's own health checks, per entry.
 *
 * Mapped in `Lighthouse.Api/Program.cs` with a JSON response writer: `{ status, totalDuration,
 * entries: { <check name>: { status, duration, description } } }`, AllowAnonymous. Every
 * non-`external` check runs (the filter matches the Azure probe's).
 */
export const HEALTH_PATH = '/api/health'

/**
 * The two statuses that carry a health report.
 *
 * ASP.NET's health-check middleware answers 200 for Healthy AND Degraded, and 503 for Unhealthy —
 * with the same per-entry body, which is exactly the one a reader needs most. So a 503 is parsed,
 * not thrown.
 */
const REPORT_STATUSES: ReadonlySet<number> = new Set([200, 503])

function isHealthReport(body: unknown): body is HealthCheckResponse {
  if (typeof body !== 'object' || body === null || Array.isArray(body)) return false
  const record = body as Record<string, unknown>
  return typeof record.status === 'string'
    && typeof record.entries === 'object'
    && record.entries !== null
    && !Array.isArray(record.entries)
}

function parseJson(raw: string): unknown {
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return raw
  }
}

/**
 * The API's health report, or a thrown error — never an invented "Unhealthy".
 *
 * The call this replaces (`checkHealth` in services/api.ts) returned `{ status: 'Unhealthy',
 * entries: {} }` for ANY non-2xx. A 401, or the Vite proxy's 500 when the API process is not
 * running at all, therefore rendered as a confident red verdict about checks nobody ran. The
 * difference matters: "the API says the warehouse is down" and "we could not ask the API" send an
 * operator to different places.
 *
 * - 200 or 503 with a health body: the body, as served.
 * - 200 or 503 WITHOUT one (an HTML error page, an empty body): ApiError — something other than the
 *   health middleware answered.
 * - Any other status: ApiError carrying the status and the ProblemDetails text when there is one.
 * - A network failure: the `TypeError` fetch rejected with, unchanged, so `errorKind` reads it as
 *   `network` rather than as a status.
 */
export async function checkHealth(): Promise<HealthCheckResponse> {
  const headers = await createHeaders()
  const response = await fetch(HEALTH_PATH, {
    headers,
    // 'omit' for the reason spelled out in services/api.ts fetchApi: a cookie authenticates nothing
    // here, and localhost cookies from other dev apps push requests past the dev server's header cap.
    credentials: 'omit',
  })

  const raw = await response.text().catch(() => '')
  const body = parseJson(raw)

  if (REPORT_STATUSES.has(response.status)) {
    if (isHealthReport(body)) return body
    throw new ApiError(
      response.status,
      `The health endpoint answered ${response.status} without a health report`,
      body,
    )
  }

  const detail = problemDetailText(body)
  const title = typeof body === 'object' && body !== null && !Array.isArray(body)
    && typeof (body as Record<string, unknown>).title === 'string'
    ? ((body as Record<string, unknown>).title as string)
    : null
  const fallback = response.status === 401
    ? 'Unauthorized - please sign in or check your API key'
    : response.status === 403
      ? 'Forbidden - you do not have access to this resource'
      : `Health check failed: ${response.status} ${response.statusText}`.trim()
  throw new ApiError(response.status, detail ?? fallback, body, title, detail)
}
